import type {
  NewsActivationResult,
  NewsDetailResponse,
  NewsDownloadProgress,
  NewsListFilters,
  NewsListResponse,
  NewsPreview,
  NewsPublicationStatus,
  NewsStageResult,
} from "./types";

type APIError = Error & { status?: number };

async function requestJSON<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, { credentials: "include", ...init });
  if (!response.ok) {
    let message = "Private news request failed.";
    try {
      const payload = await response.json() as { error?: string };
      if (payload.error) {
        message = payload.error;
      }
    } catch {
      // Keep the deliberately generic fallback.
    }
    const error = new Error(message) as APIError;
    error.status = response.status;
    throw error;
  }
  return response.json() as Promise<T>;
}

export function fetchNewsPublicationStatus(): Promise<NewsPublicationStatus> {
  return requestJSON("/api/swing-trading/news/publication/status");
}

export function fetchNewsDownloadProgress(): Promise<NewsDownloadProgress> {
  return requestJSON("/api/swing-trading/news/publication/download/status");
}

export function fetchNewsList(filters: NewsListFilters): Promise<NewsListResponse> {
  const params = new URLSearchParams();
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  if (filters.ticker) params.set("ticker", filters.ticker.trim().toUpperCase());
  if (filters.source) params.set("source", filters.source.trim());
  if (filters.page) params.set("page", String(filters.page));
  if (filters.pageSize) params.set("pageSize", String(filters.pageSize));
  const query = params.toString();
  return requestJSON(`/api/swing-trading/news${query ? `?${query}` : ""}`);
}

export function fetchNewsDetail(newsID: string): Promise<NewsDetailResponse> {
  return requestJSON(`/api/swing-trading/news/${encodeURIComponent(newsID)}`);
}

export function stageLatestNewsBundle(): Promise<NewsStageResult> {
  return requestJSON("/api/swing-trading/news/publication/stage", { method: "POST" });
}

export function fetchNewsPreview(bundleVersion: string): Promise<NewsPreview> {
  const params = new URLSearchParams({ bundleVersion });
  return requestJSON(`/api/swing-trading/news/publication/preview?${params.toString()}`);
}

export function activateNewsBundle(bundleVersion: string): Promise<NewsActivationResult> {
  return requestJSON("/api/swing-trading/news/publication/activate", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ bundleVersion }),
  });
}

export function rollbackNewsBundle(): Promise<NewsActivationResult> {
  return requestJSON("/api/swing-trading/news/publication/rollback", { method: "POST" });
}
